// SPDX-License-Identifier: AGPL-3.0-or-later
import { normalizeDescription, parseAmount, parseDate } from "./parse.js";
const norm = (h) => h.trim().toLowerCase();
const col = (header, name) => header.map(norm).indexOf(name);
export const PROFILES = [
    {
        id: "rbc",
        name: "RBC Royal Bank",
        verified: false,
        signature: ["account type", "account number", "transaction date", "description 1", "cad$"],
        mapping: (h) => ({
            hasHeader: true,
            dateOrder: "MDY",
            date: col(h, "transaction date"),
            description: [col(h, "description 1"), col(h, "description 2")].filter((i) => i >= 0),
            amount: col(h, "cad$"),
            usdAmount: col(h, "usd$") >= 0 ? col(h, "usd$") : undefined,
            account: col(h, "account number"),
        }),
    },
    {
        id: "tangerine",
        name: "Tangerine",
        verified: false,
        signature: ["date", "transaction", "name", "memo", "amount"],
        mapping: (h) => ({
            hasHeader: true,
            dateOrder: "MDY",
            date: col(h, "date"),
            description: [col(h, "name")],
            memo: col(h, "memo"),
            amount: col(h, "amount"),
        }),
    },
    {
        id: "scotiabank",
        name: "Scotiabank",
        verified: false,
        signature: ["date", "description", "type of transaction", "amount"],
        mapping: (h) => ({
            hasHeader: true,
            dateOrder: "YMD",
            date: col(h, "date"),
            description: [col(h, "description"), col(h, "sub-description")].filter((i) => i >= 0),
            amount: col(h, "amount"),
            direction: col(h, "type of transaction"),
        }),
    },
    {
        // Older Scotiabank exports have no header: date, amount, "-", type, description.
        id: "scotiabank-legacy",
        name: "Scotiabank (older format)",
        verified: false,
        signature: null,
        detectRow: (r) => r.length >= 5 && parseDate(r[0] ?? "") !== null && parseAmount(r[1] ?? "") !== null && r[2]?.trim() === "-",
        mapping: () => ({ hasHeader: false, dateOrder: "MDY", date: 0, amount: 1, description: [4, 3] }),
    },
    {
        id: "eq-bank",
        name: "EQ Bank",
        verified: false,
        signature: ["transfer date", "description", "amount", "balance"],
        mapping: (h) => ({
            hasHeader: true,
            dateOrder: "DMY",
            date: col(h, "transfer date"),
            description: [col(h, "description")],
            amount: col(h, "amount"),
        }),
    },
];
/** Picks the built-in profile that matches the file, or null if none does. */
export function detectProfile(rows) {
    const first = rows[0];
    if (!first)
        return null;
    const header = first.map(norm);
    let best = null;
    for (const profile of PROFILES) {
        if (profile.signature) {
            if (profile.signature.every((name) => header.includes(name))) {
                const score = profile.signature.length;
                if (!best || score > best.score)
                    best = { profile, score };
            }
        }
        else if (profile.detectRow?.(first)) {
            if (!best)
                best = { profile, score: 0 };
        }
    }
    return best ? { profile: best.profile, mapping: best.profile.mapping(first) } : null;
}
/** Reads rows with a column mapping. Rows that cannot be read are reported, never dropped silently. */
export function applyMapping(rows, mapping) {
    const transactions = [];
    const errors = [];
    const start = mapping.hasHeader ? 1 : 0;
    for (let i = start; i < rows.length; i++) {
        const row = rows[i];
        const line = i + 1;
        const date = parseDate(row[mapping.date] ?? "", mapping.dateOrder);
        if (!date) {
            errors.push({ line, message: `Unreadable date "${row[mapping.date] ?? ""}"` });
            continue;
        }
        let amount = null;
        let currency = "CAD";
        if (mapping.amount !== undefined)
            amount = parseAmount(row[mapping.amount] ?? "");
        if (amount === null && mapping.usdAmount !== undefined) {
            amount = parseAmount(row[mapping.usdAmount] ?? "");
            if (amount !== null)
                currency = "USD";
        }
        if (amount === null && (mapping.debit !== undefined || mapping.credit !== undefined)) {
            const debit = mapping.debit !== undefined ? parseAmount(row[mapping.debit] ?? "") : null;
            const credit = mapping.credit !== undefined ? parseAmount(row[mapping.credit] ?? "") : null;
            if (debit !== null || credit !== null)
                amount = (credit ?? 0) - Math.abs(debit ?? 0);
        }
        if (amount === null) {
            errors.push({ line, message: "No amount on this row" });
            continue;
        }
        if (mapping.direction !== undefined) {
            const dir = (row[mapping.direction] ?? "").trim().toLowerCase();
            if (/debit|withdrawal|purchase|payment out|retrait|débit/.test(dir))
                amount = -Math.abs(amount);
            else if (/credit|deposit|crédit|dépôt/.test(dir))
                amount = Math.abs(amount);
        }
        if (mapping.invertSign)
            amount = -amount;
        const description = mapping.description
            .map((c) => (row[c] ?? "").trim())
            .filter(Boolean)
            .join(" ");
        const memo = mapping.memo !== undefined ? (row[mapping.memo] ?? "").trim() : "";
        const account = mapping.account !== undefined ? (row[mapping.account] ?? "").replace(/\D/g, "") : "";
        transactions.push({
            date,
            amount,
            currency,
            description: description || "(no description)",
            ...(memo ? { memo } : {}),
            ...(account ? { accountHint: account.slice(-4) } : {}),
        });
    }
    return { transactions, errors };
}
/**
 * A stable id for a transaction, used to spot duplicates when statements
 * overlap. The occurrence number keeps two genuine identical purchases on
 * the same day (two coffees) apart.
 */
export function fingerprints(transactions) {
    const seen = new Map();
    return transactions.map((t) => {
        const base = `${t.date}|${t.amount}|${t.currency}|${normalizeDescription(t.description)}`;
        const n = (seen.get(base) ?? 0) + 1;
        seen.set(base, n);
        return `${base}|${n}`;
    });
}
//# sourceMappingURL=profiles.js.map